import {
	StoreSettingsEmailInvalidErrorText,
	StoreSettingsHeaderText,
	StoreSettingsLeadNotificationHelperText,
	StoreSettingsLeadNotificationMaxErrorText,
	StoreSettingsLeadNotificationMultiHelperText,
	StoreSettingsLeadSectionText,
	StoreSettingsMapSectionText,
	StoreSettingsNoDataErrorButton,
	StoreSettingsReadOnlyNoteTitleText,
	StoreSettingsReadOnlyNoteText,
	StoreSettingsRequiredFieldText,
	StoreSettingsSaveFailedText,
	StoreSettingsSaveLoadingText,
	StoreSettingsSaveSuccessText
} from "components/Preferences/text.ts";
import { NotFoundHeader, NotFoundText, ServiceUnavailableHeader, ServiceUnavailableText } from "constants/text.ts";
import { FormHelperText, MenuItem, Skeleton, TextField } from "@mui/material";
import { UnavailableDataPlaceholderText } from "constants/text.ts";
import { useLocation, useNavigate, useParams } from "react-router";
import { useContext, useEffect, useMemo, useState } from "react";
import { useGetStoreSettingsQuery, useUpdateStoreSettingsMutation } from "features/api/storesApi.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import {
	UserPermission,
	LeadAssignmentPreference,
	LanguagesType,
	LeadAssignmentPreferenceType
} from "types/declarations/enums.ts";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as Styles from "./StoreSettingsPage.styles.ts";
import APIErrorCustomBanner from "components/Common/APIErrorCustomBanner/APIErrorCustomBanner.tsx";
import { APIErrorFaceBox } from "components/Common/APIErrorFaceBox/APIErrorFaceBox.tsx";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import useSnackbarEffect from "hooks/useSnackbarEffect.ts";
import { GetStoreSettingsResponseData, UpdateStoreSettingsRequest } from "types/api/stores/stores.ts";
import InfoBanner from "components/Common/InfoBanner/InfoBanner.tsx";

const STORE_SETTINGS_MAX_EMAILS = 10;
const STORE_SETTINGS_LIST_ROUTE = "/admin-settings/stores";
const FIELD_LABELS = {
	leadAssignmentPreference: "User Lead Assignment",
	leadNotificationEmail: "New Lead Email Notifications",
	mapVisibility: "Map Visibility",
	showStoreNameAs: "Show Store Name As",
	languages: "Languages",
	installationServices: "Installation Services",
	designerServices: "Designer Services",
	locationType: "Location Type",
	hasShowroom: "Has Showroom",
	storeWebsite: "Store Website",
	storeEmail: "Store Email",
	storePhoneNumber: "Store Phone Number"
} as const;

const leadAssignmentOptions: { value: LeadAssignmentPreferenceType; label: string }[] = [
	{ value: LeadAssignmentPreference.ByAdminsOnly, label: "By Admins Only" },
	{ value: LeadAssignmentPreference.ByAnyone, label: "By Anyone" }
];

const mapVisibilityOptions = [
	{ value: true, label: "Show on Dealer Locator Map" },
	{ value: false, label: "Do Not Show on Dealer Locator Map" }
];

const installerOptions = [
	{ value: true, label: "Available" },
	{ value: false, label: "Not Available" }
];

const shrunkInputLabelProps = { shrink: true };

type LeadNotificationEmailField = {
	value: string;
};

interface StoreSettingsFormValues {
	storeName: string;
	storeInfo: {
		email: string;
		phoneNumber: string;
	};
	leadAssignmentPreference: LeadAssignmentPreferenceType;
	offerings: {
		locatorEnabled: boolean;
		supportedLanguages: LanguagesType[];
		hasInstallationServices: boolean;
		storeWebsite: string;
	};
	settings: {
		leadNotificationEmails: LeadNotificationEmailField[];
	};
}

const createEmptyEmailField = (): LeadNotificationEmailField => ({ value: "" });

const isValidEmail = (value: string) => {
	const trimmedValue = value.trim();
	const atIndex = trimmedValue.indexOf("@");
	const lastAtIndex = trimmedValue.lastIndexOf("@");
	const dotIndex = trimmedValue.lastIndexOf(".");

	return (
		atIndex > 0 &&
		atIndex === lastAtIndex &&
		dotIndex > atIndex + 1 &&
		dotIndex < trimmedValue.length - 1 &&
		!trimmedValue.includes(" ")
	);
};

const getMapVisibilityText = (value?: boolean | null) => {
	return mapVisibilityOptions.find((option) => option.value === value)?.label ?? "";
};

const getInstallerText = (value?: boolean | null) => {
	return installerOptions.find((option) => option.value === value)?.label ?? "";
};

const getBooleanText = (value?: boolean | null) => {
	if (value === true) return "True";
	if (value === false) return "False";

	return "";
};

const getSupportedLanguagesText = (value?: LanguagesType[] | null) => {
	return value?.join(", ") ?? "";
};

const getDisplayValue = (value: string | null | undefined) => {
	return value?.trim() ? value : UnavailableDataPlaceholderText;
};

const mapPageToFormValues = (page?: GetStoreSettingsResponseData): StoreSettingsFormValues => ({
	storeName: page?.storeName ?? "",
	storeInfo: {
		email: page?.storeInfo?.email ?? "",
		phoneNumber: page?.storeInfo?.phoneNumber ?? ""
	},
	leadAssignmentPreference: page?.settings?.leadAssignmentPreference ?? LeadAssignmentPreference.ByAnyone,
	offerings: {
		locatorEnabled: page?.offerings?.locatorEnabled ?? false,
		supportedLanguages: page?.offerings?.supportedLanguages ?? [],
		hasInstallationServices: page?.offerings?.hasInstallationServices ?? false,
		storeWebsite: page?.offerings?.storeWebsite ?? ""
	},
	settings: {
		leadNotificationEmails: (() => {
			const leadNotificationEmails = page?.settings?.leadNotificationEmails ?? [];

			if (leadNotificationEmails.length === 0) {
				return [createEmptyEmailField()];
			}

			return leadNotificationEmails.map((email) => ({ value: email ?? "" }));
		})()
	}
});

const buildRequest = (values: StoreSettingsFormValues): UpdateStoreSettingsRequest => ({
	settings: {
		leadAssignmentPreference: values.leadAssignmentPreference,
		leadNotificationEmails: values.settings.leadNotificationEmails
			.map(({ value }) => value.trim())
			.filter((email) => email.length > 0)
	}
});

const StoreSettingsPage = () => {
	const { storeId } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const { configurationsData } = useContext(ConfigurationsContext);
	const { data: userDetails, isLoading: isUserDetailsLoading } = useGetUserDetailsQuery();
	const storeNameFromState = (location.state as { storeName?: string } | null)?.storeName;
	const isAdmin = useMemo(
		() => userDetails?.permissions?.includes(UserPermission.ADMIN) ?? false,
		[userDetails?.permissions]
	);
	const isUserPreferenceEnabled = configurationsData?.featureFlags?.userPreferenceEnabled ?? false;
	const shouldSkipSettingsQuery = !storeId || !isAdmin || !isUserPreferenceEnabled;
	const {
		data: storeSettingsPage,
		error: storeSettingsError,
		isLoading: isStoreSettingsLoading
	} = useGetStoreSettingsQuery(storeId ?? "", { skip: shouldSkipSettingsQuery });
	const [updateStoreSettings, { isLoading: isSaving }] = useUpdateStoreSettingsMutation();
	const [saveErrorMessage, setSaveErrorMessage] = useState<string | undefined>(undefined);
	const [emailLimitError, setEmailLimitError] = useState<string | undefined>(undefined);
	const { snackbarOpen } = useSnackbarEffect(isSaving, saveErrorMessage);

	const {
		control,
		handleSubmit,
		register,
		reset,
		formState: { errors }
	} = useForm<StoreSettingsFormValues>({
		defaultValues: mapPageToFormValues()
	});
	const { fields, append, remove } = useFieldArray({
		control,
		name: "settings.leadNotificationEmails"
	});
	const hasRemovableLeadNotificationEmails = fields.length > 1;
	const leadNotificationHelperText = hasRemovableLeadNotificationEmails
		? StoreSettingsLeadNotificationMultiHelperText
		: StoreSettingsLeadNotificationHelperText;

	useEffect(() => {
		if (!storeSettingsPage) return;
		reset(mapPageToFormValues(storeSettingsPage));
		setEmailLimitError(undefined);
	}, [reset, storeSettingsPage]);

	const handleAddLeadNotificationEmail = () => {
		if (fields.length >= STORE_SETTINGS_MAX_EMAILS) {
			setEmailLimitError(StoreSettingsLeadNotificationMaxErrorText);
			return;
		}

		append(createEmptyEmailField());
		setEmailLimitError(undefined);
	};

	const handleRemoveLeadNotificationEmail = (index: number) => {
		remove(index);
		setEmailLimitError(undefined);
	};

	const onSubmit = async (values: StoreSettingsFormValues) => {
		if (!storeId) return;

		try {
			setSaveErrorMessage(undefined);
			const updatedStoreSettings = await updateStoreSettings({
				storeId,
				request: buildRequest(values)
			}).unwrap();

			if (updatedStoreSettings) {
				reset(mapPageToFormValues(updatedStoreSettings));
			}
		} catch {
			setSaveErrorMessage(StoreSettingsSaveFailedText);
		}
	};

	if (isUserDetailsLoading || isStoreSettingsLoading) {
		return (
			<Styles.StoreSettingsLoadingWrapper>
				<Skeleton
					variant="rectangular"
					height={560}
				/>
			</Styles.StoreSettingsLoadingWrapper>
		);
	}

	if (!isUserPreferenceEnabled || !isAdmin) {
		return (
			<div>
				<APIErrorCustomBanner statusCode={403} />
			</div>
		);
	}

	if (!storeId || storeSettingsError || !storeSettingsPage) {
		const statusInError =
			!!storeSettingsError && typeof storeSettingsError === "object" && "status" in storeSettingsError;
		let statusCode = 404;
		if (storeId && statusInError) {
			statusCode = Number(storeSettingsError.status);
		}
		const is404Error = statusCode === 404;

		return (
			<Styles.StoreSettingsErrorBoxWrapper>
				<APIErrorFaceBox
					buttonText={StoreSettingsNoDataErrorButton}
					buttonClick={() => navigate(STORE_SETTINGS_LIST_ROUTE)}
					headerText={is404Error ? NotFoundHeader : ServiceUnavailableHeader}
					descriptionText={is404Error ? NotFoundText : ServiceUnavailableText}
					errorCode={statusCode}
					faceBoxType={is404Error ? "notFound" : "generic"}
				/>
			</Styles.StoreSettingsErrorBoxWrapper>
		);
	}

	const pageTitle = storeNameFromState || storeSettingsPage.storeName?.trim() || storeSettingsPage.storeId || storeId;
	const dealerLocatorItems = [
		{
			label: FIELD_LABELS.mapVisibility,
			value: getDisplayValue(getMapVisibilityText(storeSettingsPage.offerings?.locatorEnabled)),
			testId: "store-settings-map-visibility"
		},
		{
			label: FIELD_LABELS.showStoreNameAs,
			value: getDisplayValue(storeSettingsPage.storeName),
			testId: "store-settings-store-name"
		},
		{
			label: FIELD_LABELS.languages,
			value: getDisplayValue(getSupportedLanguagesText(storeSettingsPage.offerings?.supportedLanguages)),
			testId: "store-settings-languages"
		},
		{
			label: FIELD_LABELS.installationServices,
			value: getDisplayValue(getInstallerText(storeSettingsPage.offerings?.hasInstallationServices)),
			testId: "store-settings-installation-services"
		},
		{
			label: FIELD_LABELS.designerServices,
			value: getDisplayValue(storeSettingsPage.designerServices),
			testId: "store-settings-designer-services"
		},
		{
			label: FIELD_LABELS.locationType,
			value: getDisplayValue(storeSettingsPage.locationType),
			testId: "store-settings-location-type"
		},
		{
			label: FIELD_LABELS.hasShowroom,
			value: getDisplayValue(getBooleanText(storeSettingsPage.offerings?.hasShowroom)),
			testId: "store-settings-has-showroom"
		},
		{
			label: FIELD_LABELS.storeWebsite,
			value: getDisplayValue(storeSettingsPage.offerings?.storeWebsite),
			testId: "store-settings-website"
		},
		{
			label: FIELD_LABELS.storeEmail,
			value: getDisplayValue(storeSettingsPage.storeInfo?.email),
			testId: "store-settings-store-email"
		},
		{
			label: FIELD_LABELS.storePhoneNumber,
			value: getDisplayValue(storeSettingsPage.storeInfo?.phoneNumber),
			testId: "store-settings-phone-number"
		}
	];

	return (
		<>
			<Styles.StoreSettingsPageHeader>
				<h1>{`${StoreSettingsHeaderText}${pageTitle}`}</h1>
			</Styles.StoreSettingsPageHeader>

			<Styles.StoreSettingsContainer
				onSubmit={handleSubmit(onSubmit)}
				data-testid="store-settings-form"
			>
				<Styles.StoreSettingsSection>
					<Styles.StoreSettingsSectionTitle>{StoreSettingsLeadSectionText}</Styles.StoreSettingsSectionTitle>
					<Styles.StoreSettingsRequiredFieldText>
						{StoreSettingsRequiredFieldText}
					</Styles.StoreSettingsRequiredFieldText>
					<Styles.StoreSettingsFieldsGrid>
						<Controller
							name="leadAssignmentPreference"
							control={control}
							render={({ field }) => (
								<TextField
									select
									fullWidth
									size="small"
									label={FIELD_LABELS.leadAssignmentPreference}
									InputLabelProps={shrunkInputLabelProps}
									value={field.value}
									onChange={field.onChange}
									inputProps={{ "data-testid": "store-settings-lead-assignment" }}
								>
									{leadAssignmentOptions.map((option) => (
										<MenuItem
											key={option.value}
											value={option.value}
										>
											{option.label}
										</MenuItem>
									))}
								</TextField>
							)}
						/>
					</Styles.StoreSettingsFieldsGrid>
					<Styles.StoreSettingsLeadEmailList>
						{fields.map((field, index) => {
							const leadNotificationEmailError =
								errors.settings?.leadNotificationEmails?.[index]?.value?.message;

							return (
								<Styles.NotificationFieldGroup key={field.id}>
									<Styles.NotificationRow $hasRemoveButton={hasRemovableLeadNotificationEmails}>
										<TextField
											fullWidth
											size="small"
											label={FIELD_LABELS.leadNotificationEmail}
											InputLabelProps={shrunkInputLabelProps}
											error={!!errors.settings?.leadNotificationEmails?.[index]?.value}
											helperText={leadNotificationEmailError ?? ""}
											inputProps={{ "data-testid": `store-settings-lead-email-${index}` }}
											{...register(`settings.leadNotificationEmails.${index}.value`, {
												validate: (value) =>
													!value?.trim() ||
													isValidEmail(value) ||
													StoreSettingsEmailInvalidErrorText
											})}
										/>
										{hasRemovableLeadNotificationEmails && (
											<Styles.InlineRemoveButton
												variant="text"
												type="button"
												onClick={() => handleRemoveLeadNotificationEmail(index)}
												data-testid={`store-settings-remove-email-${index}`}
											>
												Remove
											</Styles.InlineRemoveButton>
										)}
									</Styles.NotificationRow>
									<Styles.SectionHelperText>{leadNotificationHelperText}</Styles.SectionHelperText>
								</Styles.NotificationFieldGroup>
							);
						})}
						{emailLimitError && <FormHelperText error>{emailLimitError}</FormHelperText>}
					</Styles.StoreSettingsLeadEmailList>
					<Styles.StoreSettingsLeadActionsRow>
						<Styles.StoreSettingsAddEmailButton
							variant="text"
							type="button"
							onClick={handleAddLeadNotificationEmail}
							data-testid="store-settings-add-email-button"
						>
							+ ADD EMAIL ADDRESS
						</Styles.StoreSettingsAddEmailButton>
						<Styles.StoreSettingsTopActions data-testid="store-settings-top-actions">
							<Styles.StoreSettingsButton
								variant="outlined"
								type="button"
								onClick={() => navigate(STORE_SETTINGS_LIST_ROUTE)}
								data-testid="store-settings-cancel-button"
							>
								CANCEL
							</Styles.StoreSettingsButton>
							<Styles.StoreSettingsButton
								type="submit"
								variant="contained"
								data-testid="store-settings-save-button"
							>
								SAVE
							</Styles.StoreSettingsButton>
						</Styles.StoreSettingsTopActions>
					</Styles.StoreSettingsLeadActionsRow>
				</Styles.StoreSettingsSection>

				<Styles.StoreSettingsSection>
					<Styles.StoreSettingsInfoBanner>
						<InfoBanner
							title={StoreSettingsReadOnlyNoteTitleText}
							bodyText={StoreSettingsReadOnlyNoteText}
						/>
					</Styles.StoreSettingsInfoBanner>
					<Styles.StoreSettingsSectionTitle>{StoreSettingsMapSectionText}</Styles.StoreSettingsSectionTitle>
					<Styles.StoreSettingsDealerLocatorList data-testid="store-settings-dealer-locator-list">
						{dealerLocatorItems.map((item) => (
							<Styles.StoreSettingsDealerLocatorListItem
								key={item.testId}
								data-testid={item.testId}
							>
								<Styles.StoreSettingsDealerLocatorListLabel>
									{item.label}:
								</Styles.StoreSettingsDealerLocatorListLabel>{" "}
								{item.value}
							</Styles.StoreSettingsDealerLocatorListItem>
						))}
					</Styles.StoreSettingsDealerLocatorList>
				</Styles.StoreSettingsSection>
			</Styles.StoreSettingsContainer>

			<Snackbar
				open={snackbarOpen}
				isLoading={isSaving}
				loadingText={StoreSettingsSaveLoadingText}
				errorText={StoreSettingsSaveFailedText}
				successText={StoreSettingsSaveSuccessText}
				isError={!!saveErrorMessage}
			/>
		</>
	);
};

export default StoreSettingsPage;
